/**
 * Language settings control for Super Admin knowledge base forms.
 * Manages primary + secondary languages for multilingual content.
 */

import React, { useMemo } from 'react';
import { Card, Form, Switch, Checkbox, Space, Tooltip } from 'antd';
import { InfoCircleOutlined, GlobalOutlined } from '@ant-design/icons';
import { Controller, Control, UseFormSetValue, FieldValues, Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AVAILABLE_LANGUAGES, PRIMARY_LANGUAGE } from '@municipality/types';
import type { LanguageSettings } from '@municipality/types';

const SECONDARY_LANGUAGES = AVAILABLE_LANGUAGES.filter(
  (lang) => lang.code !== PRIMARY_LANGUAGE
);

interface LanguageSettingsControlProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>;
  setValue: UseFormSetValue<TFieldValues>;
  isReadOnly?: boolean;
  namespace?: string;
  title?: React.ReactNode;
  showHelp?: boolean;
}

export function LanguageSettingsControl<TFieldValues extends FieldValues>({
  control,
  setValue,
  isReadOnly = false,
  namespace = 'languageSettings',
  title,
  showHelp = true,
}: LanguageSettingsControlProps<TFieldValues>): React.ReactElement {
  const { t } = useTranslation('common');

  const getFieldPath = useMemo(
    () =>
      (field: keyof LanguageSettings): Path<TFieldValues> =>
        `${namespace}.${field}` as Path<TFieldValues>,
    [namespace]
  );

  const cardTitle = useMemo(
    () => (
      <Space>
        <GlobalOutlined />
        {title ?? t('form.languageSettings')}
      </Space>
    ),
    [title, t]
  );

  return (
    <Card title={cardTitle} style={{ marginBottom: '24px' }}>
      <Controller
        name={getFieldPath('isMultilingual')}
        control={control}
        render={({ field }) => (
          <Form.Item
            label={
              showHelp ? (
                <Space>
                  {t('form.multilingual')}
                  <Tooltip title={t('form.multilingualHelpText')}>
                    <InfoCircleOutlined style={{ color: '#999' }} />
                  </Tooltip>
                </Space>
              ) : (
                t('form.multilingual')
              )
            }
          >
            <Switch
              checked={field.value}
              onChange={(checked) => {
                field.onChange(checked);
                if (!checked) {
                  setValue(
                    getFieldPath('secondaryLanguages'),
                    [] as unknown as TFieldValues[Path<TFieldValues>]
                  );
                }
              }}
              disabled={isReadOnly}
              checkedChildren={t('common.enabled')}
              unCheckedChildren={t('common.disabled')}
            />
          </Form.Item>
        )}
      />
      <Controller
        name={getFieldPath('isMultilingual')}
        control={control}
        render={({ field: multilingualField }) =>
          multilingualField.value && (
            <Controller
              name={getFieldPath('secondaryLanguages')}
              control={control}
              render={({ field }) => (
                <Form.Item
                  label={
                    showHelp ? (
                      <Space>
                        {t('form.secondaryLanguages')}
                        <Tooltip title={t('form.secondaryLanguagesHelpText')}>
                          <InfoCircleOutlined style={{ color: '#999' }} />
                        </Tooltip>
                      </Space>
                    ) : (
                      t('form.secondaryLanguages')
                    )
                  }
                  help={
                    !isReadOnly && field.value?.length === 0
                      ? t('form.selectAtLeastOneLanguage')
                      : undefined
                  }
                  validateStatus={
                    !isReadOnly && field.value?.length === 0 ? 'warning' : undefined
                  }
                >
                  <Space direction="vertical" style={{ width: '100%' }}>
                    {SECONDARY_LANGUAGES.map((lang) => {
                      const isChecked = field.value?.includes(lang.code);
                      return (
                        <Checkbox
                          key={lang.code}
                          checked={isChecked}
                          onChange={(e) => {
                            const current = field.value || [];
                            if (e.target.checked) {
                              field.onChange([...current, lang.code]);
                            } else {
                              field.onChange(
                                current.filter((c: string) => c !== lang.code)
                              );
                            }
                          }}
                          disabled={isReadOnly}
                        >
                          <Space>
                            <span style={{ fontSize: '18px' }}>{lang.flag}</span>
                            <span style={{ fontWeight: isChecked ? 600 : 400 }}>
                              {lang.nativeName}
                            </span>
                            <span style={{ color: '#999' }}>({lang.name})</span>
                          </Space>
                        </Checkbox>
                      );
                    })}
                  </Space>
                </Form.Item>
              )}
            />
          )
        }
      />
    </Card>
  );
}
